import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './LandingPage.css';
import SEO from '../components/SEO';
import { Gamepad2, Users, Trophy, Activity, Dumbbell, Brain, TrendingUp, BarChart2 } from 'lucide-react';
import Mascot from '../components/Mascot';
import { ActivityCalendar } from 'react-activity-calendar';

const LandingPage = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeMascotAccessory, setActiveMascotAccessory] = useState(null);
  
  useEffect(() => {
    const rootEl = document.getElementById('root');
    if (rootEl) rootEl.classList.add('full-width');
    return () => {
      if (rootEl) rootEl.classList.remove('full-width');
    };
  }, []);
  
  // Handlers for Auth
  const handleLoginClick = (e) => {
    e.preventDefault();
    navigate('/auth', { state: { isLogin: true } });
  };

  const handleSignupClick = (e) => {
    if (e) e.preventDefault();
    navigate('/auth', { state: { isLogin: false } });
  };

  // Scroll logic for navbar & parallax
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Reveal animations
  useEffect(() => {
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting) {
          setTimeout(() => entry.target.classList.add('in'), i * 60);
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    
    const revealEls = document.querySelectorAll('.landing-page-v3 .reveal, .landing-page-v3 .hero-mockup');
    revealEls.forEach(el => revealObserver.observe(el));
    
    return () => {
      revealEls.forEach(el => revealObserver.unobserve(el));
      revealObserver.disconnect();
    };
  }, []);

  // Heatmap animation
  useEffect(() => {
    const heatmapGrid = document.getElementById('heatmapGrid');
    if (!heatmapGrid) return;
    
    const hmObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const hmCells = heatmapGrid.querySelectorAll('.hm-cell');
          hmCells.forEach((cell, i) => {
            setTimeout(() => cell.classList.add('lit'), i * 4);
          });
          hmObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    hmObserver.observe(heatmapGrid);
    return () => {
      if (heatmapGrid) hmObserver.unobserve(heatmapGrid);
      hmObserver.disconnect();
    };
  }, []);

  // Animated Counters
  useEffect(() => {
    const counters = document.querySelectorAll('.landing-page-v3 .stat-num');
    const counterObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const text = el.getAttribute('data-target');
          if (!text) return;
          
          const match = text.match(/[\d\s]+/);
          if (!match) return;
          
          const target = parseInt(match[0].replace(/\s/g, ''), 10);
          const suffixEl = el.querySelector('.unit');
          const suffix = suffixEl ? suffixEl.outerHTML : '';
          const prefix = text.startsWith('+') ? '+' : '';
          
          const duration = 4000;
          const start = performance.now();
          
          function tick(now) {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 10);
            const current = Math.round(target * eased);
            el.innerHTML = prefix + current.toLocaleString('fr-FR') + suffix;
            if (progress < 1) {
              requestAnimationFrame(tick);
            }
          }
          requestAnimationFrame(tick);
          counterObserver.unobserve(el);
        }
      });
    }, { threshold: 0.5 });
    
    counters.forEach(c => {
      if (!c.hasAttribute('data-target')) {
         c.setAttribute('data-target', c.innerHTML);
      }
      counterObserver.observe(c);
    });
    
    return () => {
      counters.forEach(c => counterObserver.unobserve(c));
      counterObserver.disconnect();
    };
  }, []);

  // Responsive Activity Calendar
  const [calendarData, setCalendarData] = useState([]);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 600);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const data = [];
    const endDate = new Date();
    const startDate = new Date();
    const monthsBack = isMobile ? 3 : 6;
    startDate.setMonth(startDate.getMonth() - monthsBack);
    
    for (let d = startDate; d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      const r = Math.random();
      
      data.push({
        date: dateStr,
        count: r > 0.6 ? 1 : 0,
        level: r > 0.6 ? 1 : 0
      });
    }
    setCalendarData(data);
  }, [isMobile]);

  return (
    <div className="landing-page-v3">
      <SEO title="Masse-terclass | Ton partenaire d'entraînement ultime" />
      
      <header id="header" className={isScrolled ? 'scrolled' : ''}>
        <nav>
          <div className="logo">
            <img loading="lazy" src="/logos/logo-officiel-transparent.png" alt="Logo" style={{ height: '36px', marginRight: '8px' }} />
            <span>Masse-terclass</span>
          </div>
          <div className="nav-actions">
            <button type="button" onClick={handleLoginClick} className="lp-btn lp-btn-ghost">Connexion</button>
            <button type="button" onClick={handleSignupClick} className="lp-btn lp-btn-primary lp-btn-signup">S'inscrire</button>
          </div>
        </nav>
      </header>

      <section className="hero">
        <div className="hero-glow" />
        <div className="wrap">
          <div className="hero-content">
            <h1>Transforme ta vision <br/><em>de la musculation.</em></h1>
            <p className="lead" style={{ maxWidth: '800px', margin: '0 auto 2rem' }}>Ne te contente plus d'aller à la salle. Suis tes performances, créé tes programmes, écrase tes records, le tout accompagné d'un coach IA.</p>
            <div className="hero-actions" style={{ justifyContent: 'center', marginBottom: '0', display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
              <button onClick={handleSignupClick} className="lp-btn lp-btn-primary" style={{ border: 'none' }}>Créer mon compte</button>
            </div>
          </div>
        </div>
      </section>

      <section className="stats">
        <div className="wrap">
          <div className="stats-grid">
            <div className="reveal">
              <div className="stat-num" data-target="100<span class='unit'>+</span>">100<span className="unit">+</span></div>
              <div className="stat-label">exercices intégrés à la bibliothèque</div>
            </div>
            <div className="reveal">
              <div className="stat-num" data-target="50<span class='unit'>+</span>">50<span className="unit">+</span></div>
              <div className="stat-label">succès à débloquer</div>
            </div>
            <div className="reveal">
              <div className="stat-num" data-target="25<span class='unit'>+</span>">25<span className="unit">+</span></div>
              <div className="stat-label">cosmétiques pour ton profil</div>
            </div>
          </div>
        </div>
      </section>

      <section id="fonctionnalites">
        <div className="wrap">
          {/* Feature 1 : Intelligence Artificielle */}
          <div className="feature-row reveal">
            <div className="feature-text">
              <div className="eyebrow" style={{ background: 'transparent', padding: 0, border: 'none', marginBottom: '1rem', color: 'var(--primary)' }}>
                <Brain size={20} /> Intelligence Artificielle
              </div>
              <h2>
                Ton coach personnel 2.0.
              </h2>
              <p style={{ color: 'var(--text-sec)', fontSize: '1.15rem', lineHeight: '1.7', marginBottom: '2rem' }}>
                L'IA analyse tes performances, calcule ton tonnage et repère tes tendances. Plus besoin de deviner : tu sais exactement quand augmenter tes poids ou quand te reposer pour maximiser tes gains.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                <li style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '1.1rem' }}><div style={{ background: 'rgba(168,159,207,0.1)', borderRadius: '50%', flexShrink: 0, width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><BarChart2 color="var(--primary)" size={20}/></div> Analyse automatique de tes séances</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '1.1rem' }}><div style={{ background: 'rgba(168,159,207,0.1)', borderRadius: '50%', flexShrink: 0, width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><TrendingUp color="var(--primary)" size={20}/></div> Prédiction de progression</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '1.1rem' }}><div style={{ background: 'rgba(168,159,207,0.1)', borderRadius: '50%', flexShrink: 0, width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Activity color="var(--primary)" size={20}/></div> Génération de programmes</li>
              </ul>
            </div>
            <div className="feature-image">
              <div className="feature-glow" style={{ background: 'radial-gradient(circle, rgba(140, 127, 191, 0.15) 0%, transparent 60%)' }} />
              
              <div className="coach-demo-card" style={{ background: 'rgba(28, 26, 39, 0.4)', border: '1px solid rgba(140, 127, 191, 0.4)', borderRadius: '24px', display: 'flex', flexDirection: 'column', gap: '1rem', position: 'relative', zIndex: 1, boxShadow: '0 20px 50px rgba(140, 127, 191, 0.15)', maxWidth: '480px', margin: '0 auto', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                  <div style={{ background: 'var(--primary)', flexShrink: 0, width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Brain size={20} color="#ffffff" /></div>
                  <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Coach IA</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1.2rem', borderRadius: '12px', borderLeft: '3px solid var(--primary)' }}>
                  <p style={{ margin: 0, fontSize: '0.95rem', color: 'var(--text)', lineHeight: '1.6' }}>
                    <strong>Bilan de la séance :</strong> Excellente séance, tu as explosé ton volume sur les pecs ! 🔥 Pour maximiser l'hypertrophie, augmente légèrement la charge la prochaine fois en visant 6 à 8 reps. 💪
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 2 : Gamification */}
          <div className="feature-row reveal" style={{ flexWrap: 'wrap-reverse' }}>
            <div className="feature-image" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="feature-glow" />
              <div className="mascot-preview-card" style={{ background: 'rgba(28, 26, 39, 0.4)', border: '1px solid var(--border)', borderRadius: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', position: 'relative', zIndex: 1, boxShadow: '0 20px 50px rgba(0,0,0,0.4)', width: '100%', maxWidth: '380px' }}>
                <Mascot id="cat" level={42} size={isMobile ? 160 : 220} interactive={false} previewAccessory={activeMascotAccessory} />
                <div style={{ width: '100%', marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '0.8rem' }}>
                  <button onClick={() => setActiveMascotAccessory(prev => prev === 'accessory_glasses' ? null : 'accessory_glasses')} style={{ padding: '8px', borderRadius: '8px', border: activeMascotAccessory === 'accessory_glasses' ? '2px solid var(--primary)' : '2px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', cursor: 'pointer', transition: 'all 0.2s', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg viewBox="50 -5 100 25" width="38" height="18">
                      <rect x="55" y="0" width="40" height="15" fill="#111" />
                      <rect x="105" y="0" width="40" height="15" fill="#111" />
                      <rect x="95" y="5" width="10" height="5" fill="#111" />
                      <polygon points="65,15 80,0 90,0 75,15" fill="white" opacity="0.3" />
                      <polygon points="115,15 130,0 140,0 125,15" fill="white" opacity="0.3" />
                    </svg>
                  </button>
                  <button onClick={() => setActiveMascotAccessory(prev => prev === 'accessory_headband' ? null : 'accessory_headband')} style={{ padding: '8px', borderRadius: '8px', border: activeMascotAccessory === 'accessory_headband' ? '2px solid var(--primary)' : '2px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', cursor: 'pointer', transition: 'all 0.2s', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg viewBox="45 -5 130 40" width="38" height="18">
                      <path d="M 50 10 Q 100 0 150 10 L 145 25 Q 100 15 55 25 Z" fill="#E74C3C" />
                      <path d="M 150 15 L 170 30 M 150 15 L 165 5" stroke="#E74C3C" strokeWidth="6" strokeLinecap="round" />
                      <rect x="90" y="5" width="20" height="15" fill="#BDC3C7" />
                    </svg>
                  </button>
                  <button onClick={() => setActiveMascotAccessory(prev => prev === 'accessory_crown' ? null : 'accessory_crown')} style={{ padding: '8px', borderRadius: '8px', border: activeMascotAccessory === 'accessory_crown' ? '2px solid var(--primary)' : '2px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', cursor: 'pointer', transition: 'all 0.2s', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg viewBox="55 5 90 50" width="28" height="23">
                      <path d="M 70 50 L 60 20 L 80 35 L 100 10 L 120 35 L 140 20 L 130 50 Z" fill="#F1C40F" />
                      <circle cx="60" cy="20" r="4" fill="#E74C3C" />
                      <circle cx="100" cy="10" r="4" fill="#E74C3C" />
                      <circle cx="140" cy="20" r="4" fill="#E74C3C" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div className="feature-text">
              <div className="eyebrow" style={{ background: 'transparent', padding: 0, border: 'none', marginBottom: '1rem' }}>
                <Gamepad2 size={20} /> Gamification
              </div>
              <h2>
                L'entraînement récompensé.
              </h2>
              <p style={{ color: 'var(--text-sec)', fontSize: '1.15rem', lineHeight: '1.7', marginBottom: '2rem' }}>
                Chaque séance et chaque goutte de sueur te rapporte de l'XP et des pièces. Dépense tes pièces dans la Boutique dédiée pour personnaliser ton profil et frimer auprès de tes potes.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                <li style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '1.1rem' }}><div style={{ background: 'rgba(168,159,207,0.1)', borderRadius: '50%', flexShrink: 0, width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Trophy color="var(--primary)" size={20}/></div> Gagne de l'XP et monte en niveau</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '1.1rem' }}><div style={{ background: 'rgba(168,159,207,0.1)', borderRadius: '50%', flexShrink: 0, width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Activity color="var(--primary)" size={20}/></div> Achète des bannières et cadres dans la boutique</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '1.1rem' }}><div style={{ background: 'rgba(168,159,207,0.1)', borderRadius: '50%', flexShrink: 0, width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Dumbbell color="var(--primary)" size={20}/></div> Débloque et personnalise ta mascotte</li>
              </ul>
            </div>
          </div>

          {/* Feature 3 : Social */}
          <div className="feature-row reveal">
            <div className="feature-text">
              <div className="eyebrow" style={{ background: 'transparent', padding: 0, border: 'none', marginBottom: '1rem', color: 'var(--secondary)' }}>
                <Users size={20} /> Communauté
              </div>
              <h2>
                L'union fait la force.
              </h2>
              <p style={{ color: 'var(--text-sec)', fontSize: '1.15rem', lineHeight: '1.7', marginBottom: '2rem' }}>
                Tu n'es plus seul face à tes poids. Retrouve tes amis, partage tes programmes, encourage-les dans le flux d'activité et hisse-toi au sommet du Leaderboard.
              </p>
              <button onClick={handleSignupClick} className="lp-btn lp-btn-ghost" style={{ border: '2px solid rgba(255,255,255,0.2)', padding: '12px 24px', background: 'transparent' }}>
                Rejoindre l'élite
              </button>
            </div>
            <div className="feature-image">
              <div className="feature-glow" style={{ background: 'radial-gradient(circle, rgba(122, 191, 141, 0.15) 0%, transparent 60%)' }} />
              <div className="leaderboard-demo-card" style={{ background: 'rgba(28, 26, 39, 0.4)', border: '1px solid var(--border)', borderRadius: '24px', display: 'flex', flexDirection: 'column', gap: '0.5rem', position: 'relative', zIndex: 1, boxShadow: '0 20px 50px rgba(0,0,0,0.4)', width: '100%', maxWidth: '400px', margin: '0 auto' }}>
                <h3 style={{ margin: '0 0 1rem', fontSize: '1.2rem', textAlign: 'center' }}>Classement Hebdomadaire</h3>
                {['Bill Ceps', 'Cléa Tine', 'Sarah Masse', 'Hal Tère'].map((name, i) => (
                  <div key={i} className="leaderboard-demo-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.9rem', background: i === 0 ? 'rgba(122, 191, 141, 0.15)' : 'rgba(0,0,0,0.2)', borderRadius: '12px', border: i === 0 ? '1px solid var(--secondary)' : '1px solid transparent' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                      <div style={{ color: i < 3 ? '#FFD700' : 'var(--text-sec)', fontWeight: 'bold' }}>#{i + 1}</div>
                      <div style={{ fontWeight: 'bold' }}>{name}</div>
                    </div>
                    <div style={{ color: 'var(--secondary)', fontWeight: 'bold' }}>{15200 - (i * 2400)} XP</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Feature 4 : Progression */}
          <div className="feature-row reveal" style={{ flexWrap: 'wrap-reverse' }}>
            <div className="feature-image">
              <div className="feature-glow" style={{ background: 'radial-gradient(circle, rgba(122, 191, 141, 0.15) 0%, transparent 60%)' }} />
              <div className="heatmap-wrap" style={{ alignItems: 'center', position: 'relative', zIndex: 1, margin: '0 auto', width: '100%', maxWidth: '100%' }}>
                {calendarData.length > 0 && (
                  <ActivityCalendar
                    data={calendarData}
                    maxLevel={1}
                    hideColorLegend={false}
                    theme={{
                      dark: ['#2f2c3d', '#6eb583']
                    }}
                    labels={{
                      totalCount: isMobile ? '{{count}} séances (3 mois)' : '{{count}} séances ces 6 derniers mois',
                      legend: { less: 'Repos', more: 'Entraînement' }
                    }}
                    colorScheme="dark"
                    showWeekdayLabels={!isMobile}
                    blockSize={isMobile ? 11 : 14}
                    blockMargin={isMobile ? 3 : 5}
                    fontSize={isMobile ? 11 : 14}
                  />
                )}
              </div>
            </div>
            <div className="feature-text">
              <div className="eyebrow" style={{ background: 'transparent', padding: 0, border: 'none', marginBottom: '1rem', color: 'var(--secondary)' }}>
                <TrendingUp size={20} /> Progression
              </div>
              <h2>
                Ta régularité, visualisée.
              </h2>
              <p style={{ color: 'var(--text-sec)', fontSize: '1.15rem', lineHeight: '1.7', marginBottom: '2rem' }}>
                La vraie mesure du progrès n'est pas un jour parfait, c'est une année qui ne décroche pas. Suis tes performances via plusieurs graphiques, tous pensés pour t'aider à visualiser tes progrès.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-final" id="cta" style={{ borderTop: '1px solid rgba(255,255,255,0.05)', position: 'relative', overflow: 'hidden' }}>
        <div className="hero-glow" style={{ top: '50%', transform: 'translate(-50%, -50%)' }} />
        <div className="wrap" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <div className="cta-box reveal" style={{ background: 'transparent', border: 'none' }}>
            <h2>Prêt à repousser tes limites?</h2>
            <p style={{ color: 'var(--text-sec)', fontSize: '1.15rem', marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 2rem' }}>Rejoins Masse-terclass et accomplis tes objectifs dès aujourd'hui.</p>
            <div className="cta-form" style={{ display: 'flex', justifyContent: 'center' }}>
              <button onClick={handleSignupClick} className="lp-btn lp-btn-primary" style={{ border: 'none' }}>Créer mon compte maintenant</button>
            </div>
          </div>
        </div>
      </section>

      <footer>
        <div className="wrap foot-row">
          <div className="logo" style={{ fontSize: '1rem', display: 'flex', alignItems: 'center' }}>
            <img loading="lazy" src="/logos/logo-officiel-transparent.png" alt="Logo" style={{ height: '40px' }} />
          </div>
          <div className="foot-links">
            <Link to="/contact">Contact</Link>
            <Link to="/cgu">CGU</Link>
            <Link to="/privacy">Confidentialité</Link>
            <Link to="/legal">Mentions légales</Link>
          </div>
          <div className="copyright">© {new Date().getFullYear()} Masse-terclass</div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
